import { getImageUrl } from '@takeshape/routing';
/*
 * The intention is to move all this to the client package in the end, but right now it's here
 * to be shared between the draftjs / mdx implementations, which span the client / server differently.
 */
import classnames from 'classnames';
import _escape from 'lodash/escape.js';
import { highlightCode } from "./highlight-code.js";
import { isRecord } from "./types.js";
export function getApplyPrefix(prefix) {
    return (className) => (className ? prefix + className : '');
}
export function attrs(obj) {
    const attrStrings = [];
    const attrNames = Object.keys(obj);
    attrNames.sort();
    for (const attrName of attrNames) {
        const value = obj[attrName];
        if (value) {
            attrStrings.push(`${attrName}="${_escape(value)}"`);
        }
    }
    return attrStrings.length ? ` ${attrStrings.join(' ')}` : '';
}
export function imageTemplate(applyPrefix, data) {
    const { caption, credit, alignment, size, asset, imageParams } = data;
    if (!asset) {
        return '';
    }
    const imageUrl = getImageUrl(asset.path, imageParams);
    let figCaption = '';
    if (caption || credit) {
        const htmlCaption = caption ? `<span class="${applyPrefix('caption')}">${caption}</span>` : '';
        const htmlCredit = credit ? `<span class="${applyPrefix('credit')}">${credit}</span>` : '';
        figCaption = `<figcaption>${htmlCaption} ${htmlCredit}</figcaption>`;
    }
    const classes = classnames(applyPrefix(alignment), applyPrefix(size));
    const classAttr = classes ? ` class="${classes}"` : '';
    const imgAttrs = attrs({
        alt: asset.description,
        title: asset.title
    });
    let image = `<img${imgAttrs} src="${imageUrl}"/>`;
    if (data.link?.url) {
        const target = data.link.external ? ' target="blank" rel="noopener noreferrer"' : '';
        image = `<a href="${data.link.url}"${target}>${image}</a>`;
    }
    return `<figure${classAttr}>${image}${figCaption}</figure>`;
}
function renderMdx(tag, attributes, data, children) {
    const tagWithAttributes = `${tag} ${attributes.map((attr) => `${attr}="${_escape(data[attr])}"`).join(' ')}`;
    if (children) {
        return `<${tagWithAttributes}>
${children.replace(/[\n\r]+$/, '')}
</${tag}>`;
    }
    return `<${tagWithAttributes}/>`;
}
export function imageTemplateMdx(applyPrefix, data) {
    return renderMdx(applyPrefix('Image'), ['id', 'caption', 'credit', 'link', 'linkisexternal', 'alignment', 'size', 'src'], data);
}
export function oembedTemplate(applyPrefix, data) {
    return `<div class="${applyPrefix('oembed')}">${data.html}</div>`;
}
export function oembedTemplateMdx(applyPrefix, data) {
    return renderMdx(applyPrefix('Oembed'), ['url', 'author_name', 'author_url', 'width', 'height', 'type', 'cache_age', 'provider_name', 'version'], data, data.html);
}
export function codeTemplate(applyPrefix, data) {
    const { text, lang } = data;
    const code = highlightCode(text, lang);
    const langClass = lang ? ` class="${applyPrefix(`language-${lang}`)}"` : '';
    return `<pre><code${langClass}>${code}</code></pre>`;
}
export function jsonTemplate(template, context) {
    const renderTemplate = (template) => {
        return template.replace(/{{(\w+)}}/g, (_, name) => context[name] ?? '');
    };
    const transform = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(transform);
        }
        if (isRecord(obj)) {
            const result = {};
            for (const key of Object.keys(obj)) {
                result[renderTemplate(key)] = transform(obj[key]);
            }
            return result;
        }
        if (typeof obj === 'string') {
            return renderTemplate(obj);
        }
        return obj;
    };
    return transform(template);
}
